import { motion } from "framer-motion";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell, Tooltip, LabelList,
} from "recharts";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CheckCircle2, ArrowRight, Calendar, TrendingDown } from "lucide-react";
import { PolicyFooter } from "@/components/PolicyFooter";

const scoreColor = (s) => (s >= 67 ? "#064E3B" : s >= 34 ? "#D4AF37" : "#B8860B");

export default function Scorecard({ scorecard, calendlyUrl, name }) {
  if (!scorecard) return null;
  const { overall_score, categories, top_leaks, next_steps, what_working } = scorecard;
  const chartData = categories.map((c) => ({ name: c.category, score: c.score }));

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-10"
      >
        <div className="inline-flex items-center gap-2 rounded-full bg-brand-green/10 text-brand-green px-4 py-1.5 text-sm font-medium mb-4">
          <TrendingDown className="w-4 h-4" /> Your Leak Scorecard
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">
          {name ? `${name}, here's` : "Here's"} your diagnosis
        </h1>
      </motion.div>

      {/* Overall */}
      <div className="rounded-2xl border border-border bg-white p-8 md:p-10 text-center shadow-sm mb-8">
        <p className="text-sm uppercase tracking-wide text-brand-ink/50 font-semibold">Overall Health Score</p>
        <div className="mt-3 flex items-end justify-center gap-2">
          <span className="font-heading text-6xl md:text-7xl font-extrabold" style={{ color: scoreColor(overall_score) }}>
            {overall_score}
          </span>
          <span className="text-2xl text-brand-ink/40 mb-2">/100</span>
        </div>
        <div className="mt-5 h-3 w-full max-w-md mx-auto bg-brand-bone rounded-full overflow-hidden border border-border">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${overall_score}%`, background: scoreColor(overall_score) }} />
        </div>
      </div>

      {/* Category chart */}
      <div data-testid="scorecard-chart" className="rounded-2xl border border-border bg-white p-6 md:p-8 shadow-sm mb-8">
        <h2 className="font-heading text-xl font-semibold mb-6">Category breakdown</h2>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
            <XAxis type="number" domain={[0, 100]} hide />
            <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 12, fill: "#022C22" }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "#FDFBF7" }} formatter={(v) => [`${v}/100`, "Score"]} />
            <Bar dataKey="score" radius={[0, 8, 8, 0]} barSize={20}>
              {chartData.map((d, i) => <Cell key={i} fill={scoreColor(d.score)} />)}
              <LabelList dataKey="score" position="right" style={{ fill: "#022C22", fontSize: 12, fontWeight: 600 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* What's working */}
      <div className="rounded-2xl border border-brand-green/20 bg-brand-green/5 p-6 md:p-8 mb-8">
        <div className="flex items-center gap-2 text-brand-green font-heading text-xl font-semibold mb-3">
          <CheckCircle2 className="w-5 h-5" /> What's working
        </div>
        <p className="text-brand-ink/80 leading-relaxed">{what_working}</p>
      </div>

      {/* Top leaks */}
      <div className="mb-8">
        <h2 className="font-heading text-2xl font-bold mb-5 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-brand-gold" /> Your Top 3 Leaks
        </h2>
        <div className="space-y-4">
          {top_leaks.map((leak, i) => (
            <div key={i} data-testid={`leak-item-${i}`} className="rounded-xl border border-border bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-4">
                <h3 className="font-heading text-lg font-semibold flex items-center gap-3">
                  <span className="w-7 h-7 rounded-full bg-brand-gold text-white text-sm flex items-center justify-center font-bold">{i + 1}</span>
                  {leak.category}
                </h3>
                <span className="text-sm font-semibold" style={{ color: scoreColor(leak.score) }}>{leak.score}/100</span>
              </div>
              <p className="mt-3 text-brand-ink/70 leading-relaxed">{leak.why}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Next steps */}
      <div className="rounded-2xl border border-border bg-white p-6 md:p-8 shadow-sm mb-8">
        <h2 className="font-heading text-2xl font-bold mb-5">Recommended next steps</h2>
        <ol className="space-y-4">
          {next_steps.map((step, i) => (
            <li key={i} className="flex gap-4">
              <span className="w-7 h-7 rounded-full bg-brand-green text-white text-sm flex items-center justify-center font-bold shrink-0">{i + 1}</span>
              <span className="text-brand-ink/80 leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* CTA */}
      <div className="rounded-2xl bg-brand-green text-white p-8 md:p-10 text-center">
        <Calendar className="w-9 h-9 text-brand-gold mx-auto" />
        <h2 className="font-heading text-2xl sm:text-3xl font-extrabold mt-4">Ready to plug these leaks?</h2>
        <p className="mt-3 text-white/80 max-w-lg mx-auto">
          Book a strategy call with Tycoon Promotions / 3E Audit and we'll help you fix
          the leaks found in your survey.
        </p>
        <a
          data-testid="scorecard-book-call"
          href={calendlyUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-6 rounded-full bg-brand-gold hover:bg-brand-goldDark text-white px-8 py-4 font-medium transition-all hover:-translate-y-0.5"
        >
          Book a Strategy Call <ArrowRight className="w-5 h-5" />
        </a>
      </div>

      <div className="text-center mt-8">
        <Button variant="ghost" onClick={() => (window.location.href = "/")} className="text-brand-ink/60" data-testid="scorecard-home-btn">
          Back to home
        </Button>
      </div>

      <PolicyFooter className="mt-6" />
    </div>
  );
}
